import React from 'react'
import { css, useTheme } from '@emotion/react'
import classNames from 'classnames'
import Nav from 'react-bootstrap/Nav'
import Navbar from 'react-bootstrap/Navbar'
import Offcanvas from 'react-bootstrap/Offcanvas'
import logo from 'images/numark-logo-white.svg'
import hamburger from 'images/hamburger-icon-white.svg'
import NavDropdown from 'react-bootstrap/NavDropdown'
import { ReactComponent as Chevron } from 'images/chevron.svg'
import { ReactComponent as Package } from 'images/package.svg'
import { ReactComponent as AccountIcon } from 'images/account.svg'
import { ReactComponent as PlusCircleOutline } from 'images/plus-circle-outline.svg'
import { ReactComponent as Download } from 'images/download.svg'
import { ReactComponent as ExternalLink } from 'images/external-link.svg'

const ChevronIcon = ({ className }) => <Chevron width={9} className={classNames('rotate', className)} />
const Divider = () => <NavDropdown.Divider style={{ marginInline: 28 }} />

const nav_css = theme => css({
    '& a': {
        color: theme.colors.white,
        
        fontFamily: theme.fonts.gothic,
        fontSize: 15,
        fontWeight: 700,
        fontStyle: 'normal',
        
        lineHeight: '100%',
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
        textDecoration: 'none',
        
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        '&::after': { display: 'none' },
    },

    '& svg': {
        transition: 'all .2s linear',
        position: 'relative',
        top: 2,
        '& path': {
            fill: 'white',
        },
    },

    // Padding
    '& a:not(.dropdown-item)':{
        padding: 28,
        [theme.queries.md]: { padding: 20 }
    },

    '& .dropdown-menu': {
        padding: 0,
        margin: 0,
        border: 0,
        borderRadius: 0,
    },

    // Rotation
    '& .dropdown-toggle.show': {
        background: 'hsl(210, 10%, 10%, 1)',
        '& svg.rotate': {
            transform: 'rotate(-180deg)',
            top: 1,
        }
    },
})

const subtitle_style = theme => ({
    fontFamily: theme.fonts.inter,
    fontSize: 12,
    fontWeight: 400,
    textTransform: 'none',
    letterSpacing: 'normal'
})

function AccountDropdown(){
    const theme = useTheme()

    const Item = ({ href, icon, title, subtitle }) => (
        <NavDropdown.Item href={href} rel='noreferrer noopener' target='_blank' style={{ gap: 20 }}>
            {icon}
            <span className='d-flex flex-column' style={{ gap: 9 }}>
                <span>
                    {title}
                </span>
                <span
                    className='subtitle' // transition and color
                    style={subtitle_style(theme)} // font size / weight / spacing
                >
                    {subtitle}
                </span>
            </span>
        </NavDropdown.Item>
    )

    return(
        <NavDropdown
            align='end'
            className='d-flex justify-content-center align-items-stretch'
            title={(
                <span className='d-flex align-items-center'>
                    <AccountIcon height={17.33}/>
                    <span
                        className='d-none d-md-block'
                        css={{
                            marginLeft: 12.33,
                            marginRight: 7.08,
                            [theme.queries.md]: { margin: 0 }
                        }}
                    >
                        My Account
                    </span>
                    <ChevronIcon className='d-none d-md-block'/>
                </span>
            )}
            css={{
                '& > .dropdown-menu': {
                    position: 'absolute !important',
                    '& .dropdown-item:hover': { backgroundColor: 'initial' },
                    '& a, & .subtitle, & path': { transition: 'all .25s ease' },
                    '& .subtitle': { color: '#C9C9C9' },
                    '& a:hover': {
                        color: theme.colors.primary,
                        '& path': { fill: theme.colors.primary },
                        '& .subtitle': { color: 'inherit' },
                    },
                }
            }}
        >
            <Item href='https://identity.inmusicbrands.com/auth/login' icon={<AccountIcon width={22}/>} title='Sign in' subtitle='Access your inMusic Profile' />
            <Item href='https://identity.inmusicbrands.com/auth/registration' icon={<PlusCircleOutline width={22}/>} title='Create account' subtitle='New to Numark? Create a profile' />
            <Divider />

            <Item href='https://account.inmusicbrands.com/my-products/hardware' icon={<Package width={22}/>} title='My Products' subtitle='View your registered products' />
            <Item href='https://account.inmusicbrands.com/offers' icon={<Download width={22}/>} title='Software & Offers' subtitle='Access your software and offers' />
            <Divider />
            
            <Nav.Link
                href='https://support.numark.com/en/support/home'
                rel='noreferrer noopener'
                target='_blank'
                className='d-flex align-items-center'
                css={{ '& > svg': { top: 0 }}}
                style={{ ...subtitle_style(theme), gap: 8 }}
            >
                Need help?<ExternalLink height={9} />
            </Nav.Link>
        </NavDropdown>
    )
}

export default function Navigation() {
    const theme = useTheme()

    return (
        <Navbar
            sticky='top'
            expand='xl'
            className='d-flex justify-content-between align-items-stretch p-0'
            style={{ background: theme.colors.black, backdropFilter: 'blur(30px)' }}
        >
            <Navbar.Brand
                href='/'
                className='m-0'
                style={{ placeContent: 'center' }}
                css={{ paddingInline: 20, [theme.queries.lg]: { paddingInline: 16 } }}
            >
                <img src={logo} alt='' className='d-block' height={22} />
            </Navbar.Brand>

            <Navbar.Offcanvas
                placement='end'
                className='flex-grow-0 flex-xl-row align-items-stretch'
            >
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className='invisible'>
                        Offcanvas
                    </Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body>
                    <Nav css={[nav_css, {
                        '& .dropdown-menu.show > .dropdown-item': {
                            paddingInline: 48
                        }
                    }]}>
                        <NavDropdown
                            title={<>Products<ChevronIcon/></>}
                            className='d-flex flex-column flex-xl-row align-items-stretch'
                        >
                            <NavDropdown.Item href='/product_category/controllers'>Controllers</NavDropdown.Item>
                            <NavDropdown.Item href='/product_category/mixers'>Mixers</NavDropdown.Item>
                            <NavDropdown.Item href='/product_category/speakers'>Speakers</NavDropdown.Item>
                            <NavDropdown.Item href='/product_category/headphones'>Headphones</NavDropdown.Item>
                            <NavDropdown.Item href='/product_category/microphones'>Microphones</NavDropdown.Item>
                            <NavDropdown.Item href='/product_category/turntables'>Turntables</NavDropdown.Item>
                            <NavDropdown.Item href='/product_category/accessories'>Accessories</NavDropdown.Item>
                            <NavDropdown.Item href='/product_category/cartridges'>Cartridges</NavDropdown.Item>
                            <NavDropdown.Item href='/product_category/legacy'>Legacy</NavDropdown.Item>
                        </NavDropdown>

                        <NavDropdown
                            title={<>Support<ChevronIcon/></>}
                            className='d-flex flex-column flex-xl-row align-items-stretch'
                        >
                            <NavDropdown.Item href='https://support.numark.com/' rel='noreferrer noopener' target='_blank'>Support</NavDropdown.Item>
                            <NavDropdown.Item href='/repairs'>Repairs</NavDropdown.Item>
                        </NavDropdown>
                        
                        <NavDropdown
                            title={<>Company<ChevronIcon/></>}
                            className='d-flex flex-column flex-xl-row align-items-stretch'
                        >
                            <NavDropdown.Item href='/company/about'>About Us</NavDropdown.Item>
                            <NavDropdown.Item href='https://support.numark.com/' rel='noreferrer noopener' target='_blank'>Contact Us</NavDropdown.Item>
                            <NavDropdown.Item href='https://www.inmusicbrands.com/careers/' rel='noreferrer noopener' target='_blank'>Careers</NavDropdown.Item>
                        </NavDropdown>
                        
                        <Nav.Link href='/numark-mix-academy'>Learn</Nav.Link>
                        <Nav.Link href='/dealers'>Find a dealer</Nav.Link>
                    </Nav>
                </Offcanvas.Body>
            </Navbar.Offcanvas>

            <div
                className='d-flex align-items-stretch'
                css={{ paddingRight: 20, [theme.queries.xl]: { paddingRight: 0 }}}
            >
                <Nav css={nav_css} className='flex-row'>
                    <Nav.Link
                        href='https://account.inmusicbrands.com/product-registration'
                        rel='noreferrer noopener'
                        target='_blank'
                        style={{ gap: 11 }}
                    >
                        <Package height={20}/>
                        <span className='d-none d-md-block'>Register Product</span>
                    </Nav.Link>
                    
                    <AccountDropdown />
                </Nav>
    
                <Navbar.Toggle
                    className='position-relative border-0 py-0'
                    style={{ paddingInline: 20, top: 2 }}
                >
                    <img src={hamburger} alt='' height={28} />
                </Navbar.Toggle>
            </div>
        </Navbar>
    )
}
